import { atomLogo, iplLogo } from '../util/image';
import React, { useContext, useState } from 'react';
import { Button, Col, Container, Form, Row } from 'react-bootstrap';
import { useForm } from 'react-hook-form';
import { Link } from 'react-router-dom';
import { usePlatform } from 'src/business/context/platform-context';
import { SessionContext } from 'src/business/context/session-context';
import { authServiceFactory } from 'src/business/service/auth-service';
import { OverallSpinner } from 'src/component/util/overall-spinner';

type FormValues = { email: string; password: string };

export const LoginForm = (): JSX.Element => {
	const {
		register,
		handleSubmit,
		setError,
		formState: { errors, isSubmitting },
	} = useForm<FormValues>({ mode: 'onChange' });
	const [isLoading, setIsLoading] = useState(false);
	const { invitationCode } = useContext(SessionContext);

	const { isEyeQMarketplace, isAtom, isIpl } = usePlatform();

	const buttonColorClass = isEyeQMarketplace
		? 'btn-color-wincanton'
		: isAtom
			? 'btn-color-apollo'
			: isIpl
				? 'btn-color-ipl'
				: 'btn-color-zeus';

	const logoImg = isAtom ? atomLogo : iplLogo;

	const handleLogin = async (data: FormValues): Promise<void> => {
		try {
			setIsLoading(true);
			const { email, password } = data;
			await authServiceFactory()
				.login({ email, password, invitationCode })
				.catch(() => {
					setError(
						'password',
						{ type: 'focus', message: 'Incorrect email or password' },
						{ shouldFocus: true },
					);
				});
		} finally {
			setIsLoading(false);
		}
	};

	return (
		<Container>
			{(isAtom || isIpl) && (
				<Row className="pt-5">
					<Col xs={1}></Col>
					<Col xs={10}></Col>
				</Row>
			)}
			{(isAtom || isIpl) && (
				<Row className="pt-5">
					<Col xs={1}></Col>
					<Col xs={10}>
						<h1>
							<img src={logoImg} />
						</h1>
					</Col>
				</Row>
			)}
			<Row className="pt-5">
				<Col xs={1}></Col>
				<Col xs={10}>
					<h1 className={`${isAtom || isIpl ? '' : 'margin-top pt-5'}`}>
						<b className="font-44">Login</b>
					</h1>
					<OverallSpinner isDisplayed={isLoading}>
						<Form onSubmit={handleSubmit(handleLogin)}>
							<Form.Group className="form-group margin-top">
								<Form.Label>Email</Form.Label>
								<Form.Control
									type="text"
									isInvalid={!!errors.email}
									required={true}
									{...register('email', {
										required: 'You must specify an email address',
										pattern: {
											value: /\S+@\S+\.\S+/,
											message: 'Entered value does not match email format',
										},
									})}
								/>
								<Form.Control.Feedback type="invalid">
									{errors.email && <p>{errors.email.message}</p>}
								</Form.Control.Feedback>
							</Form.Group>
							<Form.Group className="form-group">
								<Form.Label>Password</Form.Label>
								<Form.Control
									type="password"
									isInvalid={!!errors.password}
									required={true}
									autoComplete="off"
									{...register('password', { required: 'You must specify a password' })}
								/>
								<Form.Control.Feedback type="invalid">
									{errors.password && <p>{errors.password.message}</p>}
								</Form.Control.Feedback>
							</Form.Group>
							<Form.Group className="d-flex align-items-center justify-content-between margin-top">
								<Link
									className="unstyled-link"
									to="/reset-password"
								>
									Forgot password?
								</Link>
								<Button
									className={`btn-lg btn-primary continue-button ${buttonColorClass}`}
									type="submit"
									disabled={isLoading || isSubmitting}
								>
									Login
								</Button>
							</Form.Group>
						</Form>
					</OverallSpinner>
				</Col>
			</Row>
		</Container>
	);
};
